var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import clsx from 'clsx';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { ClientTopView } from '@/components/pages/top/ClientTopView';
import Layout from '@/container/Layout';
import DocumentBodyState from '@/helpers/window/DocumentBodyState';
import { useDidMount } from '@/hooks/lifecycle/useDidMount';
import { useTopPageFetchCase } from '@/hooks/pages/top/useTopPageFetchCase';
import { useTopPageFetchTheme } from '@/hooks/pages/top/useTopPageFetchTheme';
import { useBoolState } from '@/hooks/state/useBoolState';
import { useTypeSelector } from '@/hooks/state/useTypeSelector';
import { useHeaderState } from '@/hooks/ui/Header/useHeaderState';
import { useSetHeaderViewModeJudgeRef } from '@/hooks/ui/Header/useSetHeaderViewModeJudgeRef';
import { useClientTopPageFetchColumn } from '@/pages/top/ClientTop/useClientTopPageFetchColumn';
import { useClientTopPageSeminar } from '@/pages/top/ClientTop/useClientTopPageSeminar';
import { PROFFIT_DEFAULT_OGP } from '@/utils/constants/assets/s3';
import { CLASS_NAME_HEADER } from '@/utils/constants/element/layout';
import { scrollTo } from '@/utils/libs/react-scroll/animateScroll';
import { isGuest, isProfessional } from '@/utils/resources/auth/authResource';
import { getAbsolutePath, topPath } from '@/utils/router/links';
const title = 'PROFFIT｜課題や費用に合ったベストなコンサル選定を支援';
const description = 'PROFFITはコンサル選定支援サービスです。累計80億円以上のコンサル相談実績があり、500社以上のコンサルティング会社から依頼内容や費用水準に合わせて最適なコンサルチームを選定することが出来ます。';
/**
 * TOPページ
 * @returns
 */
export const ClientTopPage = (props) => {
    const { location: { hash, pathname }, } = props;
    const dispatch = useDispatch();
    const auth = useTypeSelector((state) => state.auth);
    const isGuestUser = isGuest(auth);
    const isProfessionalUser = isProfessional(auth);
    const headerViewModeJudgeRef = useRef();
    const setJudgeRef = useSetHeaderViewModeJudgeRef({
        judgeRef: headerViewModeJudgeRef,
    });
    const headerState = useHeaderState({ targetRef: headerViewModeJudgeRef });
    // 新規登録元の種類
    const [registrationType, changeRegistrationType] = useState('');
    // フルスクリーン画像ビューワーに表示する画像一覧
    const [fullScreenImages, changeFullScreenImages] = useState([]);
    // フルスクリーン画像ビューワーの表示
    const { boolState: isShowFullScreenImageViewer, changeFalse: hideFullScreenImageViewer, changeTrue: showFullScreenImageViewer, } = useBoolState();
    // 新規登録フォームモーダルの表示
    const { boolState: isShowRegistrationFormModal, changeFalse: hideRegistrationFormModal, changeTrue: showRegistrationFormModal, } = useBoolState();
    // body からデータを取得
    const { news: { dataList: newsList }, professional: { totalCount: professionalCount }, rfp: { totalCount: rfpCount }, } = DocumentBodyState.attributes;
    // セミナーの情報取得
    const { fetchSeminar, onChangeDocumentImage, onClickDocumentLink, onClickDocumentLinkByGuest, onClickSeminarLink, seminarList, } = useClientTopPageSeminar({
        changeFullScreenImages,
        changeRegistrationType,
        dispatch,
        isShowFullScreenImageViewer,
        showFullScreenImageViewer,
        showRegistrationFormModal,
    });
    // コラムの情報取得
    const { columnList, fetchColumn } = useClientTopPageFetchColumn({ dispatch });
    // テーマの情報取得
    const { fetchTheme, themeList } = useTopPageFetchTheme({ dispatch });
    // 選定支援事例の情報取得
    const { caseList, fetchCase } = useTopPageFetchCase({ dispatch });
    useDidMount(() => {
        ;
        (() => __awaiter(void 0, void 0, void 0, function* () {
            const apis = [];
            // state にデータがない場合はセミナーの値を取得
            if (!seminarList && !isProfessionalUser) {
                apis.push(fetchSeminar());
            }
            // state にデータがない場合はコラムの値を取得
            if (!columnList) {
                apis.push(fetchColumn());
            }
            // sessionStorage にデータがない場合はテーマの値を取得
            if (!themeList) {
                apis.push(fetchTheme());
            }
            // sessionStorage にデータがない場合は選定支援事例一覧の値を取得
            if (!caseList) {
                apis.push(fetchCase());
            }
            // apiを実行してデータを取得
            yield Promise.all(apis);
        }))();
    });
    // 対象の要素までスクロールさせる
    const topPageScroll = useCallback((targetHash) => {
        const targetElement = document.getElementById(targetHash.replace('#', ''));
        const headerElement = document.querySelector(`.${CLASS_NAME_HEADER}`);
        if (targetElement && headerElement) {
            // 指定要素 + ヘッダーの高さを考慮した高さまでスクロールさせる
            const { top } = targetElement.getBoundingClientRect();
            const { scrollTop } = document.documentElement;
            const { offsetHeight: headerHeight } = headerElement;
            const toY = top + scrollTop - headerHeight;
            scrollTo(toY, { smooth: true });
        }
    }, []);
    // ヘッダーメニューのクリックのイベントの上書き
    const onClickGuestHeaderMenu = useCallback((e) => {
        const urlParts = new URL(e.currentTarget.href);
        if (urlParts.pathname === pathname && urlParts.hash === hash) {
            // 自分と同じページのリンクを押した場合はスクロールだけさせる
            // 実際にはページを移動しているのではなく、react-routerでページを再描画させているだけのためハッシュ先に遷移しない
            // そのためpreventDefaultなしだとスクロールが最上部に戻ってしまう
            // e.preventDefault()
            topPageScroll(urlParts.hash);
        }
    }, [hash, pathname, topPageScroll]);
    // ハッシュがある場合は該当のセクションまでスクロール
    useLayoutEffect(() => {
        topPageScroll(hash);
    }, [hash, topPageScroll]);
    return (_jsxs(Layout, Object.assign({ guestHeaderViewMode: headerState.isTargetScrolled ? 'normal' : 'expand', headerType: 'top', onClickGuestHeaderMenu: onClickGuestHeaderMenu }, { children: [_jsxs(Helmet, { children: [_jsx("title", { children: title }), _jsx("meta", { content: description, name: 'description' }), _jsx("meta", { content: title, property: 'og:title' }), _jsx("meta", { content: description, property: 'og:description' }), _jsx("meta", { content: 'website', property: 'og:type' }), _jsx("meta", { content: 'PROFFIT', property: 'og:site_name' }), _jsx("meta", { content: getAbsolutePath(topPath()), property: 'og:url' }), _jsx("meta", { content: PROFFIT_DEFAULT_OGP, property: 'og:image' })] }), _jsx(ClientTopView, { caseList: caseList, 
                // ポップアップ表示の要素と被らないようにpaddingを設定
                className: clsx('lg:pb-0'), columnList: columnList, fullScreenImages: fullScreenImages, hideFullScreenImageViewer: hideFullScreenImageViewer, hideRegistrationFormModal: hideRegistrationFormModal, isGuest: isGuestUser, isProfessional: isProfessionalUser, isShowFullScreenImageViewer: isShowFullScreenImageViewer, isShowRegistrationFormModal: isShowRegistrationFormModal, mainVisualSize: isGuestUser ? 'large' : 'normal', newsList: newsList, onChangeSlideImage: onChangeDocumentImage, onClickDocumentLink: isGuestUser ? onClickDocumentLinkByGuest : onClickDocumentLink, onClickSeminarLink: onClickSeminarLink, professionalCount: professionalCount, registrationType: registrationType, rfpCount: rfpCount, seminarList: seminarList, setHeaderJudgeRef: setJudgeRef, themeList: themeList })] })));
};
